/**
 * What every trial page does on arrival: load, move focus, and handle the two
 * ways a link can be dead.
 *
 * Extracted because five pages need the identical behaviour and the expired
 * case is the one most likely to be forgotten: it is a designed screen with a
 * recovery step, not an error state, so a page that treats it as a generic
 * failure silently strands somebody whose link merely aged out.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Move focus to the page's h1 after a navigation.
 *
 * The handoff asks for this explicitly. Without it a keyboard or screen-reader
 * user who follows a link lands at the top of the document and has to walk
 * through the whole header again on every page.
 */
export function useFocusHeading(ready) {
  const ref = useRef(null);
  useEffect(() => {
    if (ready && ref.current) ref.current.focus();
  }, [ready]);
  return ref;
}

/**
 * Load one trial endpoint.
 *
 * @param {() => Promise<any>} loader called with an AbortSignal
 * @returns {{data, error, loading, reload}} `error.code` is what the page
 *   branches on: `TRIAL_LINK_EXPIRED` is the recovery screen.
 */
export function useTrialData(loader, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setLoading(true);
    setError(null);

    loader({ signal: controller.signal })
      .then((payload) => { if (live) setData(payload); })
      .catch((err) => {
        // An aborted request is this component unmounting, not a failure the
        // visitor should ever see.
        if (!live || err?.code === 'ERR_CANCELED' || controller.signal.aborted) return;
        setError(err);
      })
      .finally(() => { if (live) setLoading(false); });

    return () => { live = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

/** Relative time, in the handoff's phrasing ("2 hours ago"). */
export function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * The feed row's source line, assembled from the parts the API returns.
 *
 * Returned as parts rather than a string because the count is styled
 * differently from the names, and because "+N more" must be absent rather than
 * zero when there are three publishers or fewer.
 */
export function sourceLineParts(story) {
  const names = story?.publishers || [];
  const more = story?.morePublishers || 0;
  const tail = [...names, ...(more > 0 ? [`+${more} more`] : [])];
  return {
    count: `${story?.articleCount || 0} article${story?.articleCount === 1 ? '' : 's'}`,
    names: tail.join(' · '),
  };
}
